import { getDefaultStore } from 'jotai'
import { dbAtom, storesDictAtom, organizationAtom, userInDifferentTimeZoneAtom } from '../global/AtomsProvider'
import { getLocalStoreDate, dateRangeWithTimezoneAndAutoclose, getDailyStatID } from './timezoneFunctions'
import { validateDailyStats } from './validationFunctions'
import { addDays, addHours, format } from 'date-fns'
import * as Sentry from '@sentry/react-native'
import { getUniversalAggregation } from '../global/Firebase/firebase'
import { reportFirestoreOnlyWriterUsage } from '../global/analytics/firestoreOnlyWriterUsage'

const VALIDATION_INTERVAL_HOURS = 3

/**
 * Collections whose missing-sum-index degradation has already been reported this app session.
 *
 * Module-level, so it resets when the app restarts — which is when a newly-created index would
 * first be worth re-reporting against. See the fallback in `getCountAndSumFromQuery`.
 */
const reportedUnavailableSums = new Set()

/**
 * Clear that set. Exported so the once-per-session claim above can be TESTED — a dedup that is
 * only asserted by the comment describing it is the failure mode this file exists to remove, and
 * without a seam every test after the first would silently observe the suppressed path. Not called
 * by app code.
 */
export const resetSumAvailabilityReporting = () => reportedUnavailableSums.clear()

/** Currency tolerance — amounts are JS numbers, so this absorbs float noise and nothing else. */
const AMOUNT_TOLERANCE = 0.01

/**
 * ATL-1013 — the window of business days this reconciler will pick up beyond "today".
 *
 * WHY A FUTURE SIDE. A sale is booked onto the business day of ITS OWN device's clock. A till
 * whose clock runs ahead therefore writes real sales onto a forward-dated `dailyStats` document,
 * and until now nothing ever reconciled those: this check only ever validated the day the
 * VALIDATING device believes is today (plus yesterday, once). At ИП Смак three tills were
 * trading on three different dates at once — 1-9, 2-9 and 3-9-2026 — and only 1-9, the day a
 * correctly-clocked client called "today", was ever reconciled. The 2 sales that a Firestore
 * batch replay had double-counted on 2-9 sat in the merchant's Sales report unreachable.
 *
 * The past side covers the mirror case (a till running behind) and a day whose drift appeared
 * after its last check.
 *
 * MAX_EXTRA_DAYS_PER_RUN bounds the cost: each extra day loads that day's sales. Days are
 * stamped once reconciled, so the steady state is zero extra days per run.
 *
 * THE STAMP IS ITS OWN FIELD. `lastValidationCheck` is the TODAY path's field: `shouldValidate`
 * reads it as "when was this day last checked" and `checkDailyStatsValidation` reads its absence
 * as `isFirstValidation`, which is what triggers the once-per-day pass over YESTERDAY. Stamping
 * a forward-dated day with it would therefore make that day arrive already "validated": when
 * real time reaches it, `isFirstValidation` is false, the yesterday pass never runs, and — since
 * yesterday's own doc was stamped back when IT was today — nothing reconciles yesterday after it
 * closes. So the sweep writes `lastUnreconciledCheck` and treats EITHER stamp as reconciled.
 */
export const UNRECONCILED_WINDOW_PAST_DAYS = 2
export const UNRECONCILED_WINDOW_FUTURE_DAYS = 5
export const MAX_EXTRA_DAYS_PER_RUN = 2

/**
 * Choose the data-bearing business days that "today + yesterday" will never reach.
 *
 * Pure so the selection can be tested without Firestore.
 *
 * @param {Array<{id: string, date: Date, lastValidationCheck?: Date|null, lastUnreconciledCheck?: Date|null}>} docs dailyStats docs
 * @param {{now: Date, excludeIds: string[], max?: number, isEligible?: ((doc: {id: string, date: Date}) => boolean)|null}} options
 * @returns {Array<{id: string, date: Date}>} newest first, capped at `max`
 */
export const selectUnreconciledDays = (docs, { now, excludeIds = [], max = MAX_EXTRA_DAYS_PER_RUN, isEligible = null }) => {
  const excluded = new Set(excludeIds.filter(Boolean))
  const earliest = addDays(now, -UNRECONCILED_WINDOW_PAST_DAYS)
  const latest = addDays(now, UNRECONCILED_WINDOW_FUTURE_DAYS)

  return (docs || [])
    .filter((doc) => doc && doc.id && doc.date instanceof Date && !isNaN(doc.date.getTime()))
    .filter((doc) => !excluded.has(doc.id))
    // A day that has already been reconciled — by the today/yesterday path or by an earlier
    // sweep — is not picked up again. That is what makes this converge instead of re-loading the
    // same days' sales every three hours.
    .filter((doc) => !doc.lastValidationCheck && !doc.lastUnreconciledCheck)
    .filter((doc) => doc.date >= earliest && doc.date <= latest)
    // Eligibility is decided BEFORE the cap. A day this run can never validate must not occupy
    // one of the `max` slots: a store with two permanently-skippable documents in the window
    // (an autoclose store routinely has them — see dayIdMatchesItsOwnDate) would otherwise
    // starve every real candidate behind them, on every run, forever, and the sweep would look
    // healthy while reconciling nothing.
    .filter((doc) => !isEligible || isEligible(doc))
    .sort((a, b) => b.date - a.date)
    .slice(0, max)
}

/** Read the dailyStats documents in the reconciliation window for one store. */
const getWindowDailyStats = async (db, storeId, now) => {
  const snapshot = await db
    .collection('dailyStats')
    .where('store', '==', storeId)
    .where('date', '>=', addDays(now, -UNRECONCILED_WINDOW_PAST_DAYS))
    .where('date', '<=', addDays(now, UNRECONCILED_WINDOW_FUTURE_DAYS))
    .orderBy('date', 'asc')
    .get()

  const docs = []
  snapshot.forEach((doc) => {
    const data = doc.data() || {}
    docs.push({
      id: doc.id,
      date: data.date?.toDate ? data.date.toDate() : (data.date instanceof Date ? data.date : null),
      lastValidationCheck: data.lastValidationCheck || null,
      lastUnreconciledCheck: data.lastUnreconciledCheck || null,
    })
  })
  return docs
}

/**
 * Does this document's own `date` resolve to this document's id under BOTH namings?
 *
 * There are two. `getDailyStatID` is autoclose-AWARE — it books the post-midnight, pre-cutoff tail
 * onto the previous business day, and it is what `saveNewSale` stamps, so it names the document a
 * day's increments landed on. `validateDailyStats` — the function that actually recomputes and
 * OVERWRITES — resolves its document with the autoclose-NAIVE
 * `format(getLocalStoreDate(store, date), 'd-M-y') + '-' + store`, because that is the spelling its
 * comparison window agrees with: `dateRangeWithTimezoneAndAutoclose` takes `localDate`'s CALENDAR
 * day D and returns `[D + autoclose, D+1 + autoclose)`, i.e. business day D, for every time of day.
 *
 * `dailyStats.date` is the last sale's `saleDate` (`sales.ts:1013`, `merge: true`), so on an
 * autoclose store it routinely sits in that post-midnight tail: for business day D the stored
 * `date` is D+1 01:30. Such a document satisfies the aware clause (`getDailyStatID` → D = its id)
 * and fails the naive one — and if the sweep took it, `validateStoreForDate` would derive a window
 * for business day D+1 and compare it against document D's counters, then hand the same instant to
 * `validateDailyStats`, which would recompute D+1's window and write it onto document D+1 while the
 * sweep reported and stamped D. Requiring both namings to agree makes that case a skip.
 *
 * ⚠️ ATL-1101 TRIED TO DROP THE NAIVE CLAUSE AND IT WAS WRONG. The premise was that
 * `validateDailyStats` had been made autoclose-aware too, so the clause protected nothing. The
 * direction was backwards: the aware spelling is the one that disagrees with the window, and making
 * `validateDailyStats` aware would have moved a business day of revenue onto the previous day on
 * every recalculate of every autoclose store (see the table in validationFunctions.js). With the
 * window still keyed on the calendar day, this clause is what keeps the sweep off the documents the
 * pair cannot address.
 *
 * The cost is real and measured, and it is the price of failing closed. At ресторан Марракеш
 * (`autoCloseShiftTime: 03:00`, UTC+6, the worst-affected org in the fleet), measured 2026-09-02
 * against `qarasay-kz` by driving the real `getDailyStatID` / `getLocalStoreDate` over its 40 most
 * recent `dailyStats`: the guard passes 31 and skips 9 (22.5%), and all nine are the post-midnight
 * tail — 00:20 to 02:14 store-local, every one before the 03:00 cutoff. Stable across the device
 * timezone (identical under Etc/GMT-6, Asia/Almaty, UTC and Europe/Moscow). Reaching those nine
 * needs `validateDailyStats` to derive `newRange` from `getStoreBusinessDate` so the window and the
 * writer's naming agree — a change across every caller of it, not a clause removal here.
 */
const dayIdMatchesItsOwnDate = (storeId, date, docId) =>
  getDailyStatID(storeId, date) === docId &&
  `${format(getLocalStoreDate(storeId, date), 'd-M-y')}-${storeId}` === docId

/**
 * Platform-aware count+sum aggregation, with the money axis DEGRADING rather than taking the count
 * down with it.
 *
 * ATL-1101: this used to return the count alone, which is why the check below could only ever ask
 * "are there as many sales as the counter says". A drift that leaves the count right and the MONEY
 * wrong — a doubled `saleAmount`, an employee column that alone ran ahead, a `markSalePaid` that
 * moved an amount and not a quantity — passed it silently, and the money is the figure a merchant
 * actually reads off the Sales report.
 *
 * ⚠️ THE SUM NEEDS AN INDEX THE COUNT DOES NOT, AND PROD DOES NOT HAVE IT.
 *
 * Firestore requires an aggregated field to be in the index backing the query. Measured against
 * `qarasay-kz` on 2026-09-01 for `organizations/<org>/sales` filtered by `store` + `saleDate`:
 *
 *     count()                          → OK
 *     count() + sum(finalTotalCartPrice) → FAILED_PRECONDITION: the query requires an index
 *     sum(finalTotalCartPrice) alone     → FAILED_PRECONDITION
 *     complexSales count() + sum(finalPrice) → FAILED_PRECONDITION
 *
 * Two composite indexes are needed — `sales(store, saleDate, finalTotalCartPrice)` and
 * `complexSales(store, createdDate, finalPrice)`. This repo manages none (`firebase.json` has no
 * `indexes` block and there is no `firestore.indexes.json`), so they are created from the console
 * link in the error, the way every other index in this project was.
 *
 * Until they exist, a bare rethrow would have been catastrophic: `countActualSales` rethrows,
 * `validateStoreForDate` has no try/catch around it, and `checkDailyStatsValidation`'s outer catch
 * returns `{checked: false}` — so the whole sweep dies for every store, `lastValidationCheck` is
 * never written (no 3-hour backoff), and it re-issues the failing query on every MainStats mount,
 * indistinguishable in telemetry from a clean fleet. That is a WORSE version of the defect this
 * change exists to fix: an instrument that silently stops looking.
 *
 * So the money axis degrades. On any aggregation failure it falls back to the count-only query that
 * works today, says so in `amountAvailable`, and reports once per collection — and it lights up on
 * its own the moment the indexes are created, with no further deploy.
 */
const getCountAndSumFromQuery = async (collectionPath, whereConditions, sumField) => {
  try {
    const results = await getUniversalAggregation(collectionPath, whereConditions, [
      { type: 'count', alias: 'count' },
      { type: 'sum', alias: 'total', field: sumField },
    ])
    return { count: results.count || 0, total: results.total || 0, amountAvailable: true }
  } catch (aggregationError) {
    // Deliberately catches EVERY failure of the combined query, not only the missing index: the new
    // axis must never be able to take the existing one down, whatever went wrong. If the count-only
    // fallback fails too, that is today's behaviour and it rethrows.
    const results = await getUniversalAggregation(collectionPath, whereConditions, [
      { type: 'count', alias: 'count' },
    ])
    const collection = collectionPath.split('/').pop() ?? collectionPath
    // Once per collection per app session, as the docblock above promises — the code has to keep
    // that promise, not merely state it. The indexes are absent fleet-wide today, so this path is
    // taken on EVERY store on EVERY sweep, every 3 hours, for as long as they are missing; sending
    // an event each time would be a permanent per-device firehose reporting one unchanging fact.
    // Sentry's `fingerprint` groups those into one ISSUE but still ingests every event.
    if (!reportedUnavailableSums.has(collection)) {
      reportedUnavailableSums.add(collection)
      Sentry.captureMessage('dailyStats sweep: the money axis is unavailable, falling back to the count', {
        level: 'warning',
        tags: { daily_stats_sum_unavailable: true },
        fingerprint: ['daily-stats-sum-unavailable', collection],
        contexts: {
          daily_stats_sum: {
            collection,
            sumField,
            // The console link that creates the missing index travels in this message.
            error: aggregationError?.message ?? String(aggregationError),
          },
        },
      })
    }
    return { count: results.count || 0, total: 0, amountAvailable: false }
  }
}

/**
 * Count actual sales for a specific store and date
 */
const countActualSales = async (storeId, salesStartTime, salesEndTime) => {
  try {
    const jotaiStore = getDefaultStore()
    const organization = jotaiStore.get(organizationAtom)
    
    // Count regular sales
    const salesCollectionPath = `organizations/${organization.id}/sales`
    const salesWhereConditions = [
      { field: 'store', operator: '==', value: storeId },
      { field: 'saleDate', operator: '>=', value: salesStartTime },
      { field: 'saleDate', operator: '<', value: salesEndTime }
    ]
    
    const regularSales = await getCountAndSumFromQuery(salesCollectionPath, salesWhereConditions, 'finalTotalCartPrice')
    
    // Count complex sales
    const complexSalesCollectionPath = `organizations/${organization.id}/complexSales`
    const complexSalesWhereConditions = [
      { field: 'store', operator: '==', value: storeId },
      { field: 'createdDate', operator: '>=', value: salesStartTime },
      { field: 'createdDate', operator: '<', value: salesEndTime }
    ]
    
    // `complexSales` carry their money in `finalPrice`, not `finalTotalCartPrice` — the same split
    // `dailyStatIncrementsForComplexSales` makes when it writes `saleAmount`.
    const complexSales = await getCountAndSumFromQuery(complexSalesCollectionPath, complexSalesWhereConditions, 'finalPrice')

    return {
      regularSalesCount: regularSales.count,
      complexSalesCount: complexSales.count,
      totalCount: regularSales.count + complexSales.count,
      regularSalesAmount: regularSales.total,
      complexSalesAmount: complexSales.total,
      totalAmount: regularSales.total + complexSales.total,
      // BOTH halves, or the total is short by whichever one degraded — and a short total reads as
      // drift. The money comparison is only made when this is true.
      amountAvailable: regularSales.amountAvailable && complexSales.amountAvailable
    }
  } catch (error) {
    console.error('Error counting actual sales:', error)
    throw error
  }
}

/**
 * Check if more than 3 hours passed since last validation check
 */
const shouldValidate = (lastValidationCheck) => {
  if (!lastValidationCheck) {
    return true // First time validation
  }
  
  const lastCheck = lastValidationCheck.toDate()
  const nextCheckTime = addHours(lastCheck, VALIDATION_INTERVAL_HOURS)
  const now = new Date()
  
  return now >= nextCheckTime
}

/**
 * Validate a specific date for a store
 * @param {string} storeId - Store ID to validate
 * @param {Date} targetDate - Date to validate
 * @param {string} dateLabel - Label for logging (e.g., "today", "yesterday")
 * @returns {Object} - Validation result
 */
const validateStoreForDate = async (storeId, targetDate, dateLabel = 'target date') => {
  const jotaiStore = getDefaultStore()
  const db = jotaiStore.get(dbAtom)
  const organization = jotaiStore.get(organizationAtom)
  
  const localDate = getLocalStoreDate(storeId, targetDate)
  // ⚠️ KNOWN MISPAIRING, PRE-EXISTING AND DELIBERATELY NOT CHANGED HERE — ATL-1122.
  //
  // This is autoclose-AWARE while `dateRange` below is the CALENDAR day of `localDate`, which is
  // the same naive/aware mismatch ATL-1101 fixed one function down in `validateDailyStats`. On the
  // sweep path it is unreachable: `dayIdMatchesItsOwnDate` admits only documents where the two
  // namings agree. On the today/yesterday paths it is reachable, and on a store with an
  // `autoCloseShiftTime` whose till is open between local midnight and the cutoff it compares
  // business day D's sales against document D−1's counters — a false drift equal to a whole day's
  // sale count, reported under a document id the window never covered.
  //
  // Measured: 17 stores across 14 orgs have a non-zero cutoff (`getStoreBusinessDate` treats
  // `value: 0` as none), including ресторан Марракеш at 03:00 — whose tills this branch measured
  // trading 00:20–02:14 local. It is NOT introduced here (identical in master at 521c02f7), but
  // ATL-1101 is what starts alerting on it, so the false drift now reaches Sentry.
  //
  // Not fixed in this change because the correct fix is not local: `todayDailyStatId` below keys
  // the 3-hour throttle and the sweep's `excludeIds` off the same aware naming, so moving one
  // without the others splits the throttle from the document it throttles. Filed as ATL-1122.
  const dailyStatId = getDailyStatID(storeId, targetDate)
  
  // Get daily stat for the target date
  const dailyStatDoc = await db.collection('dailyStats')
    .doc(dailyStatId)
    .get()
  
  const dailyStatData = dailyStatDoc.exists ? dailyStatDoc.data() : null
  
  console.log(`🔍 Validating ${dateLabel} for store ${storeId} (${localDate.toDateString()})...`)
  
  // Get date range for validation
  const dateRange = dateRangeWithTimezoneAndAutoclose(
    { startDate: localDate, endDate: localDate }, 
    [storeId]
  )
  
  // Get daily stats sales count AND amount. Both, because the merchant reads both: the report at
  // ИП Смак was wrong by 2 invoices AND by 54,169 ₸, and the money is what they noticed.
  const dailyStatsSalesCount = dailyStatData?.saleQuantity || 0
  const dailyStatsSalesAmount = dailyStatData?.saleAmount || 0
  
  // Count actual sales
  const actualSalesData = await countActualSales(
    storeId, 
    dateRange.startDate, 
    dateRange.endDate
  )
  
  const countDelta = dailyStatsSalesCount - actualSalesData.totalCount
  // `null`, not 0, when the sum could not be read: 0 is a real answer meaning "the money agrees",
  // and reporting an unavailable axis as agreement is the failure this whole change is about.
  const amountAvailable = actualSalesData.amountAvailable === true
  const amountDelta = amountAvailable ? dailyStatsSalesAmount - actualSalesData.totalAmount : null
  const hasDiscrepancy =
    countDelta !== 0 || (amountAvailable && Math.abs(amountDelta) > AMOUNT_TOLERANCE)
  
  // Create validation result
  const validationResult = {
    checkedAt: new Date(),
    targetDate: localDate,
    dateLabel,
    dailyStatsSalesCount,
    actualSalesCount: actualSalesData.totalCount,
    regularSalesCount: actualSalesData.regularSalesCount,
    complexSalesCount: actualSalesData.complexSalesCount,
    dailyStatsSalesAmount,
    actualSalesAmount: amountAvailable ? actualSalesData.totalAmount : null,
    countDelta,
    amountDelta,
    amountAvailable,
    hasDiscrepancy,
    // Set below from what `validateDailyStats` actually DID — not from the fact that it was called.
    repairApplied: false,
    repairSkipped: null,
    storeId,
    dailyStatId,
    // Whether reconciliation actually got to run. A day with no discrepancy needs none, so it is
    // reconciled by definition; a day WITH one is only reconciled if `validateDailyStats` ran to
    // completion. It bails out early — no `constants.paymentMethods`, no loaded users, a failed
    // `getSalesOnDateRange`, or the Supabase-read refusal — for reasons that are all reachable
    // right after app start, and it names the reason in `meta.skipped` rather than returning
    // `undefined` (ATL-1101). The sweep must not stamp a day on the strength of a call that never
    // looked at it.
    reconciliationRan: !hasDiscrepancy
  }
  
  if (hasDiscrepancy) {
    console.warn(`⚠️  Discrepancy found in store ${storeId} for ${dateLabel}:`)
    console.warn(`📊 Daily Stats ${dailyStatId}: ${dailyStatsSalesCount} / ${dailyStatsSalesAmount}`)
    console.warn(`📈 Actual Sales ${dateRange.startDate.toLocaleDateString()} - ${dateRange.endDate.toLocaleDateString()}: ${actualSalesData.totalCount} / ${actualSalesData.totalAmount}`)
    console.warn(`📋 Regular: ${actualSalesData.regularSalesCount}, Complex: ${actualSalesData.complexSalesCount}`)
    console.warn(`📐 Δcount ${countDelta}, Δamount ${amountAvailable ? amountDelta : 'unavailable (no index)'}`)
    
    // Trigger full validation with auto-fix
    console.log(`🔧 Running full validation with auto-fix for ${dateLabel}...`)
    try {
      const fullValidationResult = await validateDailyStats(
        targetDate, 
        storeId, 
        true, // update (auto-fix)
        { sales: true, spendings: true, workingHours: true }, 
        null, // prefetchedData
        true, // log
        true, // automaticFixes
        'dailyStatsValidationSimple' // screenName
      )
      
      // ATL-1101 — read what the repair REPORTS, not the fact that it was invoked. This branch used
      // to log "✅ Applied fixes" whenever the recompute disagreed with the doc, which is true even
      // when the write was suppressed (device timezone ≠ store timezone) or the function declined to
      // look at all. A success line that prints identically whether or not anything happened is not
      // evidence of anything.
      // Destructured through an `Array.isArray` guard, not directly. `validateDailyStats` now
      // returns a 4-tuple from every exit, but this is the boundary between two modules and a
      // non-array here (a stub, an older bundle, a future exit that forgets) must degrade to "did
      // not reconcile" rather than throw a TypeError into the catch below, where it would be
      // reported to Sentry as a repair failure that never happened.
      const [isValid, fixes, , meta] = Array.isArray(fullValidationResult) ? fullValidationResult : []
      validationResult.repairApplied = Boolean(meta?.applied)
      validationResult.repairSkipped = meta?.skipped ?? null
      // ATL-1013's sweep stamp, read off the SAME meta rather than off the return's shape.
      //
      // `validateDailyStats` used to return a bare `undefined` from every early bail-out, so
      // `Array.isArray(result)` WAS the signal separating "checked and reconciled" from "could not
      // check". ATL-1101 gave those exits a uniform 4-tuple (they were a latent TypeError in
      // `compareProductReportAndDailyStats`, which destructures the result directly), which makes
      // `Array.isArray` unconditionally true — it would stamp, permanently and on every device,
      // exactly the days that were never looked at. `meta.skipped` is now what names a bail-out.
      validationResult.reconciliationRan = Array.isArray(fullValidationResult) && !meta?.skipped
      if (!Array.isArray(fullValidationResult)) {
        validationResult.repairSkipped = 'repair-returned-no-result'
        console.warn(`⏭️  Repair returned no result for store ${storeId} ${dateLabel}`)
      } else if (meta?.skipped) {
        console.warn(`⏭️  Repair declined for store ${storeId} ${dateLabel}: ${meta.skipped}`)
      } else if (meta?.applied) {
        console.log(`✅ Applied fixes for store ${storeId} ${dateLabel}:`, fixes)
      } else if (!isValid) {
        console.warn(`🚫 Fixes computed but NOT written for store ${storeId} ${dateLabel}:`, fixes)
      } else {
        // The detector and the repair DISAGREED: the aggregation says this day drifted, and the
        // recompute found nothing to correct. Silence here would read exactly like a clean repair,
        // so name it — it means one of the two is looking at the wrong set of documents.
        console.warn(`❓ Sweep saw a discrepancy the recompute did not for store ${storeId} ${dateLabel}: Δcount ${countDelta}, Δamount ${amountDelta}`)
      }
    } catch (fullValidationError) {
      validationResult.repairSkipped = 'repair-threw'
      validationResult.reconciliationRan = false
      console.error(`❌ Full validation failed for store ${storeId} ${dateLabel}:`, fullValidationError)
      Sentry.captureException(fullValidationError, {
        tags: { daily_stats_repair_failed: true },
        contexts: { daily_stats_repair: { storeId, dailyStatId, dateLabel } },
      })
    }

    // Report the DETECTION independently of the repair. `validateDailyStats` reports the keys it
    // corrected, but it never runs when it declines — and a drift nobody can see is how ATL-779 got
    // from its first confirmed instance to its second without a fleet number.
    // A drift the repair CANNOT act on in this read mode is still worth measuring — it is the
    // rollback-readiness figure — but it must not sit in the same bucket as one somebody can clear.
    // A `warning` that can never be actioned and never goes away trains people to ignore the tag,
    // and that is how this class stayed invisible across two ATL-779 instances.
    const actionable = validationResult.repairSkipped !== 'supabase-read-mode'
    Sentry.captureMessage('dailyStats disagreed with the sale documents that back it (sweep)', {
      level: actionable ? 'warning' : 'info',
      tags: {
        daily_stats_sweep_discrepancy: true,
        daily_stats_repair_applied: validationResult.repairApplied,
        daily_stats_repair_actionable: actionable,
        daily_stats_amount_axis: amountAvailable ? 'available' : 'unavailable',
      },
      fingerprint: ['daily-stats-sweep-discrepancy', storeId, actionable ? 'actionable' : 'unactionable'],
      contexts: {
        daily_stats_sweep: {
          dailyStatId,
          storeId,
          dateLabel,
          dailyStatsSalesCount,
          actualSalesCount: actualSalesData.totalCount,
          countDelta,
          dailyStatsSalesAmount,
          actualSalesAmount: amountAvailable ? actualSalesData.totalAmount : null,
          amountDelta,
          amountAvailable,
          repairApplied: validationResult.repairApplied,
          repairSkipped: validationResult.repairSkipped,
        },
      },
    })
  } else {
    // ATL-1101 — say WHICH axes were checked. Neither composite index exists in prod, so
    // `amountAvailable` is false on every store on every run today: this line is printed for a day
    // whose money was never read, in wording identical to a day whose money was read and agreed.
    // That is the defect this whole change exists to remove, in its own success line.
    console.log(
      amountAvailable
        ? `✅ Store ${storeId} ${dateLabel}: No discrepancies found (count and amount)`
        : `✅ Store ${storeId} ${dateLabel}: count agrees — AMOUNT NOT CHECKED (no index)`,
    )
  }
  
  return validationResult
}

/**
 * Simple validation check for MainStats screen
 * @param {string} storeId - Store ID to validate
 * @returns {Object} - Validation result
 */
export const checkDailyStatsValidation = async (storeId) => {
  try {
    if (!storeId) {
      return { 
        checked: false, 
        reason: 'No store ID provided' 
      }
    }

    const jotaiStore = getDefaultStore()
    const db = jotaiStore.get(dbAtom)
    const organization = jotaiStore.get(organizationAtom)
    
    if (!organization?.id) {
      return { 
        checked: false, 
        reason: 'No organization found' 
      }
    }

    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    const todayLocalDate = getLocalStoreDate(storeId, today)
    const todayDailyStatId = getDailyStatID(storeId, today)
    
    // Get current daily stat to check validation timing
    const dailyStatDoc = await db.collection('dailyStats')
      .doc(todayDailyStatId)
      .get()
    
    const dailyStatData = dailyStatDoc.exists ? dailyStatDoc.data() : null
    const isFirstValidation = !dailyStatData?.lastValidationCheck
    
    // Check if validation is needed (3+ hours since last check)
    if (!isFirstValidation && !shouldValidate(dailyStatData?.lastValidationCheck)) {
      const lastCheck = dailyStatData?.lastValidationCheck?.toDate()
      const nextCheck = addHours(lastCheck || new Date(), VALIDATION_INTERVAL_HOURS)
      
      console.log(`⏰ Store ${storeId}: Validation not due yet (last check: ${lastCheck ? lastCheck.toLocaleString() : 'never'}, next due: ${nextCheck.toLocaleString()})`)
      
      return {
        checked: false,
        reason: 'Validation not due yet',
        lastCheck,
        nextCheck
      }
    }

    if (isFirstValidation) {
      console.log(`🆕 Store ${storeId}: First validation - checking today and yesterday`)
    } else {
      console.log(`🔄 Store ${storeId}: Regular validation (last check: ${dailyStatData?.lastValidationCheck?.toDate()?.toLocaleString()})`)
    }
    
    const validationResults = []
    let totalDiscrepancies = 0
    // ATL-1101 — repairs are counted SEPARATELY from detections. They are not the same number: on a
    // cut-over org every repair returns `skipped: 'supabase-read-mode'`, so a run can detect several
    // days and write none.
    let totalRepairsApplied = 0
    
    // Always validate today
    const todayResult = await validateStoreForDate(storeId, today, 'today')
    validationResults.push(todayResult)
    if (todayResult.hasDiscrepancy) totalDiscrepancies++
    if (todayResult.repairApplied) totalRepairsApplied++
    
    // If first validation, also validate yesterday
    const yesterdayDailyStatId = getDailyStatID(storeId, yesterday)
    if (isFirstValidation) {
      const yesterdayResult = await validateStoreForDate(storeId, yesterday, 'yesterday')
      validationResults.push(yesterdayResult)
      if (yesterdayResult.hasDiscrepancy) totalDiscrepancies++
      if (yesterdayResult.repairApplied) totalRepairsApplied++
    }

    // ATL-1013 — reconcile the days that HAVE DATA, not only the days this device calls today.
    // A till whose clock runs ahead books real sales onto a forward-dated business day, which no
    // correctly-clocked client ever validates; that is how a double-counted day stayed wrong in
    // the merchant's Sales report indefinitely. Bounded and self-terminating: each day is stamped
    // once reconciled, so a healthy store adds no extra work here.
    try {
      // `validateStoreForDate` calls `validateDailyStats` with automaticFixes = true, and that
      // function declines to WRITE when the device is in a different timezone from the store
      // (`!userInDifferentTimeZone || !automaticFixes` gates its only set()). It still returns
      // normally, so from here a refusal is indistinguishable from a repair. Sweeping from such a
      // device could only ever stamp days it had no ability to reconcile — and a stamped day is
      // never offered again, on any device. Leave them to one that can actually repair them.
      //
      // This bounds the sweep only. Today's own document is still stamped with
      // `lastValidationCheck` below by the pre-existing today path, from this same device, and
      // `selectUnreconciledDays` excludes on EITHER stamp — so a store whose only device sits in
      // another timezone can still retire its current day unrepaired. Narrowing that means
      // splitting `lastValidationCheck` into a throttle field and a reconciliation-evidence
      // field, since `shouldValidate` reads it as the 3-hour throttle. Out of scope here.
      // Skipping is expressed as an empty window rather than an early return: this block sits
      // inside checkDailyStatsValidation, and returning from here would also skip today's own
      // `lastValidationCheck` write below.
      const deviceCanReconcile = !jotaiStore.get(userInDifferentTimeZoneAtom)
      if (!deviceCanReconcile) {
        console.log(`⏭️  Store ${storeId}: device timezone differs from the store; skipping the unreconciled-day sweep`)
      }
      const windowDocs = deviceCanReconcile ? await getWindowDailyStats(db, storeId, today) : []
      const extraDays = selectUnreconciledDays(windowDocs, {
        now: today,
        excludeIds: [todayDailyStatId, isFirstValidation ? yesterdayDailyStatId : null],
        // The day's own `date` is what produced its id; if it does not reproduce it — under
        // EITHER naming, see dayIdMatchesItsOwnDate — the document is mis-keyed and belongs to
        // validateDailyStatsId, not here. Validating it would write the recomputed totals of one
        // business day onto another. Nothing stamps such a document, so it stays selectable
        // forever: it is refused here, ahead of the per-run cap, rather than inside the loop.
        isEligible: (doc) => {
          if (dayIdMatchesItsOwnDate(storeId, doc.date, doc.id)) return true
          console.warn(`⏭️  Store ${storeId}: ${doc.id} does not match its own date; skipping`)
          return false
        },
      })
      for (const day of extraDays) {
        const extraResult = await validateStoreForDate(storeId, day.date, `unreconciled ${day.id}`)
        validationResults.push(extraResult)
        if (extraResult.hasDiscrepancy) totalDiscrepancies++
        // Counted here too, not only on the today/yesterday path. Leaving it out made the run
        // summary say "N discrepancies found, 0 repaired" for a swept day that WAS repaired —
        // the same "the number does not describe what happened" defect ATL-1101 exists to remove,
        // in the branch that reaches the days nothing else can.
        if (extraResult.repairApplied) totalRepairsApplied++
        // Stamp ONLY a day reconciliation actually reached. The stamp is permanent — a stamped
        // day is filtered out of `selectUnreconciledDays` on every future run and every other
        // device — so writing it after a call that bailed out early would silently retire the
        // very day this sweep exists to reach, while reporting success. A day whose
        // reconciliation DID run is stamped even if drift remains: that residual belongs to the
        // backend repair tool's class, not this one's, and leaving it selectable would starve
        // every other candidate behind MAX_EXTRA_DAYS_PER_RUN for ever.
        if (!extraResult.reconciliationRan) {
          console.warn(`⏭️  Store ${storeId}: ${day.id} not reconciled (validation did not run); leaving it selectable`)
          continue
        }
        // Deliberately NOT `lastValidationCheck` — see the docblock on the window constants.
        reportFirestoreOnlyWriterUsage('checkDailyStatsValidation', organization.id)
        await db.collection('dailyStats').doc(day.id).set({
          lastUnreconciledCheck: new Date(),
        }, { merge: true })
      }
    } catch (unreconciledError) {
      // Never let the extra sweep take down the ordinary today/yesterday check — but do not let
      // it fail SILENTLY either: a missing index or a rules change would make this sweep
      // permanently inert with nothing but a console line on a device nobody is reading.
      console.error(`❌ Store ${storeId}: unreconciled-day sweep failed:`, unreconciledError)
      Sentry.captureException(unreconciledError, {
        tags: { atl_1013: 'unreconciled_sweep_failed', books_write_error: 'daily-stats-unreconciled-sweep' },
        extra: { storeId },
      })
    }

    // Update today's daily stats with validation timestamp
    reportFirestoreOnlyWriterUsage('checkDailyStatsValidation', organization.id)
    await db.collection('dailyStats')
      .doc(todayDailyStatId)
      .set({
        lastValidationCheck: new Date(),
        lastValidationResult: {
          checkedAt: new Date(),
          isFirstValidation,
          datesValidated: validationResults.length,
          totalDiscrepancies,
          totalRepairsApplied,
          validationResults
        }
      }, { merge: true })
    
    // A run in which no day's money could be read is NOT a run that found the money clean — and
    // that is every run until the two composite indexes exist. `amountAvailable` travels on every
    // result, so the summary can say so rather than let a count-only pass read as a full one.
    const daysWithAmount = validationResults.filter((r) => r.amountAvailable).length
    const amountCoverage =
      daysWithAmount === validationResults.length
        ? ''
        : `, amount checked on ${daysWithAmount}/${validationResults.length} days (no index)`
    const summaryMessage = isFirstValidation 
      ? `First validation completed for store ${storeId}: ${validationResults.length} days checked, ${totalDiscrepancies} discrepancies found, ${totalRepairsApplied} repaired${amountCoverage}`
      : `Validation completed for store ${storeId}: ${totalDiscrepancies} discrepancies found, ${totalRepairsApplied} repaired${amountCoverage}`
    
    console.log(`📊 ${summaryMessage}`)
    
    return {
      checked: true,
      isFirstValidation,
      datesValidated: validationResults.length,
      hasDiscrepancy: totalDiscrepancies > 0,
      totalDiscrepancies,
      totalRepairsApplied,
      // How many of the days checked had their MONEY read, not just their count. `hasDiscrepancy:
      // false` with `amountAxisDays` short of `datesValidated` means "the count agrees and the
      // money is unknown" — a different claim from "clean", and the caller must be able to tell.
      amountAxisDays: daysWithAmount,
      validationResults,
      storeId
    }
    
  } catch (error) {
    console.error(`❌ Validation error for store ${storeId}:`, error)
    return {
      checked: false,
      error: error.message,
      storeId
    }
  }
}

/**
 * Validate all organization stores (for MainStats screen)
 * @returns {Object} - Validation summary
 */
export const validateAllOrgStores = async () => {
  try {
    const jotaiStore = getDefaultStore()
    const storesDict = jotaiStore.get(storesDictAtom)
    
    if (!storesDict || Object.keys(storesDict).length === 0) {
      return {
        checked: false,
        reason: 'No stores found'
      }
    }
    
    const storeIds = Object.keys(storesDict)
    console.log(`🏪 Validating ${storeIds.length} organization stores...`)
    
    const results = {}
    let checkedCount = 0
    let discrepanciesFound = 0
    let repairsApplied = 0
    let firstTimeValidations = 0
    let totalDaysValidated = 0
    let daysWithAmountAxis = 0
    
    for (const storeId of storeIds) {
      const result = await checkDailyStatsValidation(storeId)
      results[storeId] = result
      
      if (result.checked) {
        checkedCount++
        totalDaysValidated += result.datesValidated || 1
        if (result.isFirstValidation) {
          firstTimeValidations++
        }
        if (result.hasDiscrepancy) {
          discrepanciesFound += result.totalDiscrepancies || 1
        }
        repairsApplied += result.totalRepairsApplied || 0
        daysWithAmountAxis += result.amountAxisDays || 0
      }
    }
    
    const summary = {
      checked: true,
      totalStores: storeIds.length,
      storesChecked: checkedCount,
      discrepanciesFound,
      repairsApplied,
      firstTimeValidations,
      totalDaysValidated,
      // Of `totalDaysValidated`, how many had their MONEY read. Until the two composite indexes
      // exist this is 0, and a fleet summary that did not carry it would read "0 discrepancies
      // found" identically whether the money was checked and agreed or was never looked at.
      daysWithAmountAxis,
      results
    }
    
    if (checkedCount === 0) {
      console.log(`📊 Validation summary: ${checkedCount}/${storeIds.length} stores checked (all stores validated recently, within ${VALIDATION_INTERVAL_HOURS} hours)`)
    } else {
      let summaryMessage = `📊 Validation summary: ${checkedCount}/${storeIds.length} stores checked`
      if (totalDaysValidated > checkedCount) {
        summaryMessage += ` (${totalDaysValidated} days total)`
      }
      if (firstTimeValidations > 0) {
        summaryMessage += `, ${firstTimeValidations} first-time validations`
      }
      summaryMessage += `, ${discrepanciesFound} discrepancies found, ${repairsApplied} repaired`
      if (daysWithAmountAxis < totalDaysValidated) {
        summaryMessage += ` — amount checked on ${daysWithAmountAxis}/${totalDaysValidated} days (no index)`
      }
      console.log(summaryMessage)
    }
    
    return summary
    
  } catch (error) {
    console.error('❌ Error validating all stores:', error)
    return {
      checked: false,
      error: error.message
    }
  }
}

/**
 * Simple validation trigger for MainStats screen
 * @returns {Object} - Validation result
 */
export const triggerDailyStatsValidation = async () => {
  try {
    let result = await validateAllOrgStores()
    
    if (result.checked) {
      if (result.storesChecked === 0) {
        console.log(`ℹ️  No stores needed validation (last check was within ${VALIDATION_INTERVAL_HOURS} hours)`)
      } else {
        let message = ""
        if (result.firstTimeValidations > 0) {
          message += `🆕 ${result.firstTimeValidations} first-time validation(s) `
          if (result.totalDaysValidated > result.storesChecked) {
            message += `(${result.totalDaysValidated} days checked) `
          }
        }
        
        if (result.discrepanciesFound > 0) {
          // ATL-1101 — "found AND automatically fixed" was asserted from the DETECTION count, so it
          // printed unchanged for a run in which nothing was written: on a cut-over org every repair
          // returns `skipped: 'supabase-read-mode'` and this still claimed the day had been fixed.
          // Report the two numbers separately and let them differ.
          message += `⚠️  ${result.discrepanciesFound} discrepancies found, ${result.repairsApplied ?? 0} automatically fixed`
        } else {
          // NOT "successfully" when an axis was never read. `daysWithAmountAxis < totalDaysValidated`
          // is today's normal state on every org, and the two cases must not print the same string.
          message +=
            (result.daysWithAmountAxis ?? 0) < (result.totalDaysValidated ?? 0)
              ? `✅ No count discrepancies — AMOUNT NOT CHECKED on ${(result.totalDaysValidated ?? 0) - (result.daysWithAmountAxis ?? 0)}/${result.totalDaysValidated} days (no index)`
              : `✅ All stores validated successfully`
        }
        
        console.log(message.trim())
      }
    } else {
      console.log(`ℹ️  Validation skipped: ${result.reason}`)
    }
    
    return result
  } catch (error) {
    console.error('❌ MainStats validation trigger error:', error)
    return {
      checked: false,
      error: error.message
    }
  }
} 